import React, { useRef, useState, useEffect } from 'react';
import './ModernBlocks.css';

const TestimonialsBlock = ({ data }) => {
  const trackRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <svg
        key={i}
        className={`star ${i < rating ? 'star-filled' : 'star-empty'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  const scrollToSlide = (slideIndex) => {
    if (trackRef.current) {
      // Get responsive card width based on screen size
      const getCardWidth = () => {
        if (window.innerWidth <= 480) return 280; // Mobile
        if (window.innerWidth <= 768) return 300; // Tablet
        return 350; // Desktop
      };
      
      const getGap = () => {
        if (window.innerWidth <= 480) return 8; // Mobile (0.5rem)
        if (window.innerWidth <= 768) return 16; // Tablet (1rem)
        return 32; // Desktop (2rem)
      };

      const cardWidth = getCardWidth();
      const gap = getGap();
      const containerWidth = trackRef.current.parentElement.offsetWidth;
      
      // On mobile, show only one testimonial centered
      if (window.innerWidth <= 480) {
        const targetPosition = slideIndex * (cardWidth + gap);
        trackRef.current.style.transform = `translateX(-${targetPosition}px)`;
      } else {
        // Desktop/tablet: show 3 testimonials with center focus
        const totalWidth = cardWidth + gap;
        const centerPosition = (containerWidth / 2) - (cardWidth / 2);
        const targetPosition = slideIndex * totalWidth - centerPosition;
        trackRef.current.style.transform = `translateX(calc(-50% - ${targetPosition}px))`;
      }
      
      setCurrentSlide(slideIndex);
    }
  };

  const scrollLeft = () => {
    const newSlide = currentSlide > 0 ? currentSlide - 1 : data.items.length - 1;
    scrollToSlide(newSlide);
  };

  const scrollRight = () => {
    const newSlide = currentSlide < data.items.length - 1 ? currentSlide + 1 : 0;
    scrollToSlide(newSlide);
  };

  // Auto-play functionality
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        scrollRight();
      }, 5000); // Change slide every 5 seconds
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, currentSlide]);

  // Pause auto-play on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  return (
    <section className="testimonials-section">
      <div className="testimonials-container">
        <div className="testimonials-header">
          <h2 className="testimonials-title">
            {data.title}
          </h2>
          <p className="testimonials-subtitle">
            {data.subtitle}
          </p>
        </div>
        
        <div className="testimonials-slider-container" 
             onMouseEnter={handleMouseEnter}
             onMouseLeave={handleMouseLeave}>
          <button className="testimonials-slider-nav testimonials-slider-prev" onClick={scrollLeft}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <div className="testimonials-slider">
            <div className="testimonials-slider-track" ref={trackRef}>
              {data.items.map((testimonial, index) => (
                <div 
                  key={index} 
                  className={`testimonial-card ${index === currentSlide ? 'active' : 'inactive'}`}
                  onClick={() => scrollToSlide(index)}
                >
                  <div className="testimonial-stars">
                    {renderStars(testimonial.rating)}
                  </div>
                  <p className="testimonial-quote">
                    "{testimonial.quote}"
                  </p>
                  <div className="testimonial-author">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="testimonial-avatar"
                    />
                    <div className="testimonial-info">
                      <h4>{testimonial.name}</h4>
                      <p>{testimonial.position}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <button className="testimonials-slider-nav testimonials-slider-next" onClick={scrollRight}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Slide indicators */}
        <div className="testimonials-indicators">
          {data.items.map((_, index) => (
            <button
              key={index}
              className={`testimonial-indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => scrollToSlide(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsBlock; 